/** 触发设置页 — 左侧鼠标展示（仅显示触发点，不可选中） */
import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { ProductImage } from "../ProductImage";
import {
  getMouseChangeKeyLayout,
  type MouseChangeKeySpotId,
} from "@eweadn-qiankun/shared-utils";
import { PRESS_DEPTH_MAX } from "../../../../mouse-management/src/pages/triggerSetting/TriggerSetting";
import "../../../../mouse-management/src/pages/changeKey/Mouse.scss";
import "../../../../mouse-management/src/pages/triggerSetting/Mouse.scss";

const TRIGGER_SPOT_IDS = new Set<MouseChangeKeySpotId>(["left", "right"]);

/** 左右键按压深度 overlay 轮廓（改形时改 path / viewBox） */
const MOUSE_TRIGGER_PRESS_VIEWBOX = "0 0 364 709";
const MOUSE_TRIGGER_PRESS_LEFT_PATH =
  "M44.0011 707.596L362.001 706.596L362.48 439.096C350.48 438.696 334.48 435.93 327.98 434.596C310.38 430.596 306.98 410.263 307.48 400.596L308.48 207.596C305.28 191.996 313.48 181.096 317.98 177.596C323.18 170.796 349.48 168.096 361.98 167.596V1.09593C271.48 -1.9038 144.98 50.5959 102.98 76.5959C55.7803 102.996 30.989 134.263 24.4933 146.596C1.00892 224.096 0.980329 331.596 1.00112 402.596C3.4933 466.096 11.5011 590.429 20.5011 673.596C22.1011 696.396 36.8344 705.763 44.0011 707.596Z";
const MOUSE_TRIGGER_PRESS_RIGHT_PATH =
  "M319.479 707.596L1.47943 706.596L1 439.096C13 438.696 29 435.93 35.5 434.596C53.1 430.596 56.5 410.263 56 400.596L55 207.596C58.2 191.996 50 181.096 45.5 177.596C40.3 170.796 14 168.096 1.5 167.596V1.09593C92 -1.9038 218.5 50.5959 260.5 76.5959C307.7 102.996 332.491 134.263 338.987 146.596C362.472 224.096 362.5 331.596 362.479 402.596C359.987 466.096 351.979 590.429 342.979 673.596C341.379 696.396 326.646 705.763 319.479 707.596Z";

interface TriggerMouseMetricRow {
  name: string;
  value: string;
}

interface TriggerMouseButton {
  id: MouseChangeKeySpotId;
  label: string;
  metrics: TriggerMouseMetricRow[];
  position: { x: number; y: number };
  labelPosition: { x: number; y: number };
  align?: "left" | "right";
}

export interface TriggerMouseSideLabel {
  triggerPoint: number;
  fastTriggerEnabled?: boolean;
  fastTriggerPowerUi?: number;
  tactileFeedbackUi?: number;
}

export interface TriggerSettingMouseVisualizerProps {
  deviceId: string;
  displayname: string;
  left: TriggerMouseSideLabel;
  right: TriggerMouseSideLabel;
  showMagneticExtras?: boolean;
  /** 实时按压深度 0~10 */
  leftPressDepth?: number;
  rightPressDepth?: number;
  /** 为 true 时在按键上显示深度框与横线（未按下也显示框） */
  showLivePressDepth?: boolean;
}

const pressDepthToLineTopPercent = (route: number) => {
  const depth = Number.isFinite(route) ? route : 0;
  const clamped = Math.min(PRESS_DEPTH_MAX, Math.max(0, depth));
  return (clamped / PRESS_DEPTH_MAX) * 100;
};

const parsePressViewBoxSize = (viewBox: string) => {
  const parts = viewBox.trim().split(/\s+/).map(Number);
  return {
    width: Number.isFinite(parts[2]) ? parts[2] : 364,
    height: Number.isFinite(parts[3]) ? parts[3] : 709,
  };
};

const PRESS_VIEWBOX_SIZE = parsePressViewBoxSize(MOUSE_TRIGGER_PRESS_VIEWBOX);

/** 填充渐变：底边（深度横线）最深，向上渐浅，fadeEnd 以上保持最浅 */
const PRESS_FILL_GRAD_STRONG = 0.72;
const PRESS_FILL_GRAD_MID = 0.38;
const PRESS_FILL_GRAD_WEAK = 0.2;
/** 自底边向上，到 fadeEnd 才完全淡到最浅（越大过渡区越长） */
const PRESS_FILL_GRAD_FADE_MID = "38%";
const PRESS_FILL_GRAD_FADE_END = "82%";

function TriggerPressFillGradient({ id }: { id: string }) {
  return (
    <linearGradient
      id={id}
      x1="0"
      y1="1"
      x2="0"
      y2="0"
      gradientUnits="objectBoundingBox"
    >
      <stop
        offset="0%"
        stopColor="var(--primary-color)"
        stopOpacity={PRESS_FILL_GRAD_STRONG}
      />
      <stop
        offset={PRESS_FILL_GRAD_FADE_MID}
        stopColor="var(--primary-color)"
        stopOpacity={PRESS_FILL_GRAD_MID}
      />
      <stop
        offset={PRESS_FILL_GRAD_FADE_END}
        stopColor="var(--primary-color)"
        stopOpacity={PRESS_FILL_GRAD_WEAK}
      />
      <stop
        offset="100%"
        stopColor="var(--primary-color)"
        stopOpacity={PRESS_FILL_GRAD_WEAK}
      />
    </linearGradient>
  );
}

export function TriggerSettingMouseVisualizer({
  deviceId,
  displayname,
  left,
  right,
  showMagneticExtras = false,
  leftPressDepth = 0,
  rightPressDepth = 0,
  showLivePressDepth = false,
}: TriggerSettingMouseVisualizerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const hotspotsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const labelsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [lines, setLines] = useState<{
    [key: string]: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      path?: string;
    };
  }>({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const pressClipLeftId = React.useId();
  const pressClipRightId = React.useId();
  const pressFillGradLeftId = React.useId();
  const pressFillGradRightId = React.useId();
  const pressClipIds = useMemo(
    () => ({ left: pressClipLeftId, right: pressClipRightId }),
    [pressClipLeftId, pressClipRightId]
  );
  const pressFillGradIds = useMemo(
    () => ({ left: pressFillGradLeftId, right: pressFillGradRightId }),
    [pressFillGradLeftId, pressFillGradRightId]
  );

  const buildMetricRows = useCallback(
    (side: TriggerMouseSideLabel): TriggerMouseMetricRow[] => {
      const offLabel = t("mouse.trigger.vibrationOff");
      const rows: TriggerMouseMetricRow[] = [
        {
          name: t("mouse.trigger.pressForce"),
          value: String(side.triggerPoint),
        },
      ];
      if (showMagneticExtras) {
        rows.push({
          name: t("mouse.trigger.fastTriggerLabel"),
          value:
            side.fastTriggerEnabled && side.fastTriggerPowerUi != null
              ? String(side.fastTriggerPowerUi)
              : offLabel,
        });
        rows.push({
          name: t("mouse.trigger.vibrationIntensity"),
          value:
            (side.tactileFeedbackUi ?? 0) > 0
              ? String(side.tactileFeedbackUi)
              : offLabel,
        });
      }
      return rows;
    },
    [showMagneticExtras, t]
  );

  const pressDepthBySide = useMemo(
    () => ({
      left: pressDepthToLineTopPercent(leftPressDepth),
      right: pressDepthToLineTopPercent(rightPressDepth),
    }),
    [leftPressDepth, rightPressDepth]
  );

  const pressLineY = useMemo(
    () => ({
      left: (pressDepthBySide.left / 100) * PRESS_VIEWBOX_SIZE.height,
      right: (pressDepthBySide.right / 100) * PRESS_VIEWBOX_SIZE.height,
    }),
    [pressDepthBySide.left, pressDepthBySide.right]
  );

  const settingLineY = useMemo(
    () => ({
      left:
        (pressDepthToLineTopPercent(left.triggerPoint) / 100) *
        PRESS_VIEWBOX_SIZE.height,
      right:
        (pressDepthToLineTopPercent(right.triggerPoint) / 100) *
        PRESS_VIEWBOX_SIZE.height,
    }),
    [left.triggerPoint, right.triggerPoint]
  );

  const buttons: TriggerMouseButton[] = useMemo(() => {
    const spots = getMouseChangeKeyLayout(deviceId).filter((spot) =>
      TRIGGER_SPOT_IDS.has(spot.id)
    );
    return spots.map((spot) => {
      const side = spot.id === "left" ? left : right;
      return {
        ...spot,
        label:
          spot.id === "left"
            ? t("mouse.trigger.leftButton")
            : t("mouse.trigger.rightButton"),
        metrics: buildMetricRows(side),
      };
    });
  }, [buildMetricRows, deviceId, left, right, t]);

  const updateLines = useCallback(() => {
    if (!containerRef.current || !imageLoaded) return;

    requestAnimationFrame(() => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      const newLines: typeof lines = {};
      const TOP_BUTTON_IDS = new Set(["left", "middle", "right"]);
      const TOP_STEM_IDS = new Set(["left", "right"]);
      const TOP_STEM_LENGTH = (32 / 474) * containerHeight;

      buttons.forEach((button) => {
        const hotspot = hotspotsRef.current[button.id];
        const label = labelsRef.current[button.id];
        const hotspotX = (button.position.x / 100) * containerWidth;
        const hotspotY = (button.position.y / 100) * containerHeight;
        const hotspotRadius = hotspot ? hotspot.offsetWidth / 2 : 14;
        const labelAnchorX = (button.labelPosition.x / 100) * containerWidth;
        const labelAnchorY = (button.labelPosition.y / 100) * containerHeight;
        const isTopButton = TOP_BUTTON_IDS.has(button.id);
        const hasTopStem = TOP_STEM_IDS.has(button.id);
        const labelBottomOffset = label ? label.offsetHeight / 2 : 24;
        const x2 = labelAnchorX;
        const y2 = isTopButton
          ? labelAnchorY + labelBottomOffset
          : labelAnchorY;
        const targetY = y2 + (hasTopStem ? TOP_STEM_LENGTH : 0);
        const dx = x2 - hotspotX;
        const dy = targetY - hotspotY;
        const distance = Math.hypot(dx, dy) || 1;
        const x1 = hotspotX + (dx / distance) * hotspotRadius;
        const y1 = hotspotY + (dy / distance) * hotspotRadius;
        const path = hasTopStem
          ? `M ${x2} ${y2} L ${x2} ${y2 + TOP_STEM_LENGTH} L ${x1} ${y1}`
          : `M ${x2} ${y2} L ${x1} ${y1}`;

        newLines[button.id] = { x1, y1, x2, y2, path };
      });

      setLines(newLines);
    });
  }, [buttons, imageLoaded]);

  useEffect(() => {
    if (!imageLoaded) return;
    const timer = setTimeout(updateLines, 50);
    return () => clearTimeout(timer);
  }, [imageLoaded, updateLines]);

  useEffect(() => {
    if (imageLoaded) updateLines();
  }, [buttons, updateLines, imageLoaded]);

  useEffect(() => {
    if (!imageLoaded || !containerRef.current) return;
    const observer = new ResizeObserver(() => updateLines());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [imageLoaded, updateLines]);

  return (
    <div className="mouse-container trigger-mouse-readonly">
      <div className="mouse-visualizer" ref={containerRef}>
        <svg className="connection-lines-container">
          {buttons.map((button) => {
            const line = lines[button.id];
            if (!line) return null;
            if (line.path) {
              return <path key={`line-${button.id}`} d={line.path} />;
            }
            return (
              <line
                key={`line-${button.id}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
              />
            );
          })}
        </svg>

        <div className="mouse-image-wrap">
          <ProductImage
            displayname={displayname}
            alt="Mouse"
            className="mouse-image"
            onLoad={() => setImageLoaded(true)}
          />

          {showLivePressDepth ? (
            <div className="trigger-button-press-stack" aria-hidden>
              <div className="trigger-button-press-side trigger-button-press-side--left">
                <svg
                  className="trigger-button-press-art"
                  viewBox={MOUSE_TRIGGER_PRESS_VIEWBOX}
                  preserveAspectRatio="xMinYMin meet"
                >
                  <defs>
                    <clipPath
                      id={pressClipIds.left}
                      clipPathUnits="userSpaceOnUse"
                    >
                      <path d={MOUSE_TRIGGER_PRESS_LEFT_PATH} />
                    </clipPath>
                    <TriggerPressFillGradient id={pressFillGradIds.left} />
                  </defs>
                  <path
                    className="trigger-button-press-bg"
                    d={MOUSE_TRIGGER_PRESS_LEFT_PATH}
                  />
                  <g clipPath={`url(#${pressClipIds.left})`}>
                    <rect
                      className="trigger-button-press-bg-top"
                      x={0}
                      y={0}
                      width={PRESS_VIEWBOX_SIZE.width}
                      height={settingLineY.left}
                    />
                  </g>
                  <g clipPath={`url(#${pressClipIds.left})`}>
                    <path
                      className="trigger-button-press-outline"
                      d={MOUSE_TRIGGER_PRESS_LEFT_PATH}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                  <g
                    className={
                      pressDepthBySide.left > 0
                        ? "trigger-button-press-depth-group--active"
                        : "trigger-button-press-depth-group"
                    }
                    clipPath={`url(#${pressClipIds.left})`}
                  >
                    <rect
                      className="trigger-button-press-fill"
                      x={0}
                      y={0}
                      width={PRESS_VIEWBOX_SIZE.width}
                      height={pressLineY.left}
                      fill={`url(#${pressFillGradIds.left})`}
                    />
                    <line
                      className="trigger-button-press-line"
                      x1={0}
                      y1={pressLineY.left}
                      x2={PRESS_VIEWBOX_SIZE.width}
                      y2={pressLineY.left}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                  <g clipPath={`url(#${pressClipIds.left})`}>
                    <line
                      className="trigger-button-press-setting-line"
                      x1={0}
                      y1={settingLineY.left}
                      x2={PRESS_VIEWBOX_SIZE.width}
                      y2={settingLineY.left}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                </svg>
              </div>
              <div className="trigger-button-press-side trigger-button-press-side--right">
                <svg
                  className="trigger-button-press-art"
                  viewBox={MOUSE_TRIGGER_PRESS_VIEWBOX}
                  preserveAspectRatio="xMaxYMin meet"
                >
                  <defs>
                    <clipPath
                      id={pressClipIds.right}
                      clipPathUnits="userSpaceOnUse"
                    >
                      <path d={MOUSE_TRIGGER_PRESS_RIGHT_PATH} />
                    </clipPath>
                    <TriggerPressFillGradient id={pressFillGradIds.right} />
                  </defs>
                  <path
                    className="trigger-button-press-bg"
                    d={MOUSE_TRIGGER_PRESS_RIGHT_PATH}
                  />
                  <g clipPath={`url(#${pressClipIds.right})`}>
                    <rect
                      className="trigger-button-press-bg-top"
                      x={0}
                      y={0}
                      width={PRESS_VIEWBOX_SIZE.width}
                      height={settingLineY.right}
                    />
                  </g>
                  <g clipPath={`url(#${pressClipIds.right})`}>
                    <path
                      className="trigger-button-press-outline"
                      d={MOUSE_TRIGGER_PRESS_RIGHT_PATH}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                  <g
                    className={
                      pressDepthBySide.right > 0
                        ? "trigger-button-press-depth-group--active"
                        : "trigger-button-press-depth-group"
                    }
                    clipPath={`url(#${pressClipIds.right})`}
                  >
                    <rect
                      className="trigger-button-press-fill"
                      x={0}
                      y={0}
                      width={PRESS_VIEWBOX_SIZE.width}
                      height={pressLineY.right}
                      fill={`url(#${pressFillGradIds.right})`}
                    />
                    <line
                      className="trigger-button-press-line"
                      x1={0}
                      y1={pressLineY.right}
                      x2={PRESS_VIEWBOX_SIZE.width}
                      y2={pressLineY.right}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                  <g clipPath={`url(#${pressClipIds.right})`}>
                    <line
                      className="trigger-button-press-setting-line"
                      x1={0}
                      y1={settingLineY.right}
                      x2={PRESS_VIEWBOX_SIZE.width}
                      y2={settingLineY.right}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                </svg>
              </div>
            </div>
          ) : null}
        </div>

        {buttons.map((button) => (
          <div key={button.id} className="mouse-button-group">
            <div
              ref={(el) => {
                labelsRef.current[button.id] = el;
              }}
              className={`button-label ${button.align === "right" ? "align-right" : ""}`}
              style={{
                left: `${button.labelPosition.x}%`,
                top: `${button.labelPosition.y}%`,
              }}
            >
              <div className="label-text">{button.label}</div>
              {button.metrics.map((row, index) => (
                <div key={index} className="label-metric">
                  <span className="label-metric-name">{row.name}</span>
                  <span className="label-metric-value">{row.value}</span>
                </div>
              ))}
            </div>

            <div
              ref={(el) => {
                hotspotsRef.current[button.id] = el;
              }}
              className="mouse-button-hotspot"
              style={{
                left: `${button.position.x}%`,
                top: `${button.position.y}%`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
